import { useEffect } from 'react';

const navigationSelector = 'a[href], [role="link"], [data-selection-safe-navigation]';
const controlSelector = 'button, input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="button"]';
const movementThreshold = 4;
const longPressMs = 500;

function elementAt(target: EventTarget | null): Element | null {
  return target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
}

function navigationAt(target: EventTarget | null): Element | null {
  const element = elementAt(target);
  const closest = element?.closest(`${navigationSelector}, ${controlSelector}`);
  // Nested copy buttons and form controls keep their own behavior.
  return closest?.matches(navigationSelector) ? closest : null;
}

function hasSelectionWithin(element: Element): boolean {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return false;
  for (let index = 0; index < selection.rangeCount; index += 1) {
    if (selection.getRangeAt(index).intersectsNode(element)) return true;
  }
  return false;
}

// Install once at the app boundary, including links rendered in portals or lazy routes.
// Keep native selection, context menus, keyboard activation and normal modified clicks.
export default function useSelectionSafeNavigation() {
  useEffect(() => {
    let restoreNativeDrag: (() => void) | null = null;
    const restoreLinkDrag = () => { restoreNativeDrag?.(); restoreNativeDrag = null; };
    let gesture: {
      navigation: Element | null; pointerId: number; x: number; y: number;
      startedAt: number; releasedAt: number | null; moved: boolean; cancelled: boolean;
    } | null = null;

    const pointerDown = (event: PointerEvent) => {
      restoreLinkDrag();
      if (event.button !== 0 && event.button !== 1) { gesture = null; return; }
      const navigation = navigationAt(event.target);
      // Anchors implicitly drag their URL instead of selecting their label. Disable
      // that default only for this text gesture; explicit drag/drop and images stay native.
      if (navigation instanceof HTMLAnchorElement && !navigation.hasAttribute('draggable')
        && navigation.textContent?.trim() && !elementAt(event.target)?.closest('img, svg')) {
        navigation.draggable = false;
        restoreNativeDrag = () => { navigation.removeAttribute('draggable'); };
      }
      gesture = {
        navigation, pointerId: event.pointerId,
        x: event.clientX, y: event.clientY, startedAt: Date.now(),
        releasedAt: null, moved: false, cancelled: false,
      };
    };
    const updateMovement = (event: PointerEvent) => {
      if (!gesture || event.pointerId !== gesture.pointerId || gesture.releasedAt !== null) return;
      if (Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > movementThreshold) gesture.moved = true;
    };
    const pointerUp = (event: PointerEvent) => {
      updateMovement(event);
      if (gesture && event.pointerId === gesture.pointerId) gesture.releasedAt = Date.now();
      restoreLinkDrag();
    };
    const pointerCancel = (event: PointerEvent) => {
      if (gesture && event.pointerId === gesture.pointerId) {
        gesture.cancelled = true;
        gesture.releasedAt = Date.now();
        restoreLinkDrag();
      }
    };
    const click = (event: MouseEvent) => {
      // Keyboard and assistive activation have no pointer gesture.
      if (event.detail === 0 && !(event as PointerEvent).pointerType) return;
      if (event.button !== 0 && event.button !== 1) return;
      const navigation = navigationAt(event.target);
      if (!navigation) { gesture = null; return; }
      const now = Date.now();
      const currentGesture = gesture;
      gesture = null;
      const sameGesture = currentGesture?.navigation === navigation
        && (currentGesture.releasedAt === null || now - currentGesture.releasedAt < 1000);
      if (hasSelectionWithin(navigation) || (sameGesture && currentGesture && (
        currentGesture.moved || currentGesture.cancelled
        || (currentGesture.releasedAt ?? now) - currentGesture.startedAt >= longPressMs
      ))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const reset = () => { gesture = null; restoreLinkDrag(); };
    // Window capture runs before React's delegated handlers and native anchor activation.
    window.addEventListener('pointerdown', pointerDown, true);
    window.addEventListener('pointermove', updateMovement, true);
    window.addEventListener('pointerup', pointerUp, true);
    window.addEventListener('pointercancel', pointerCancel, true);
    window.addEventListener('click', click, true);
    window.addEventListener('auxclick', click, true);
    window.addEventListener('blur', reset);
    return () => {
      reset();
      window.removeEventListener('pointerdown', pointerDown, true);
      window.removeEventListener('pointermove', updateMovement, true);
      window.removeEventListener('pointerup', pointerUp, true);
      window.removeEventListener('pointercancel', pointerCancel, true);
      window.removeEventListener('click', click, true);
      window.removeEventListener('auxclick', click, true);
      window.removeEventListener('blur', reset);
    };
  }, []);
}
